import { formatNumber, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  forwardRef, inject, Input, OnDestroy, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Editor, JSONContent } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import {
  ANNOUNCEMENT_BODY_MAX_CHARS,
  ANNOUNCEMENT_RICH_TEXT_EXTENSIONS,
  richTextLength
} from '../announcement-list/announcement-rich-text';

/** Beschriftung der Link-Abfrage. */
const LINK_PROMPT_KEY = 'core.announcement.form.editor.linkPrompt';

// Client-Dok §4: der Zaehler zeigt deutsche Tausendertrennzeichen ("1.240 / 10.000"), unabhaengig
// von der gerade eingestellten UI-Sprache — genau wie moment.locale('de') in AppModule Datums-
// formate bereits app-weit auf Deutsch festlegt, unabhaengig von der Uebersetzung. registerLocaleData
// macht die 'de'-Daten fuer formatNumber() verfuegbar, ohne den globalen LOCALE_ID-Provider
// anzufassen (das TourPlanningModule tut exakt das bereits, dort aber nur fuer sein eigenes Modul).
registerLocaleData(localeDe, 'de');

/**
 * Rich-Text-Eingabe fuer Ankuendigungs-Bodies (#738-Nachzug, Client-Dok §8.1).
 *
 * BEWUSST eine eigene Komponente statt eines konfigurierbaren nx-edit-tipTap-input: die
 * Allow-List ist kein Geschmack, sondern eine Server-Schranke. Ein Editor, der sie per Input
 * abwaehlen kann, laedt genau den Fehler ein, den §8.1 beschreibt — „ein Editor, der mehr
 * anbietet, produziert zuverlaessig Inhalte, die der Server ablehnt".
 *
 * Der Wert ist ein ProseMirror-Dokument, kein String und kein HTML.
 */
@Component({
  selector: 'nx-announcement-body-input',
  templateUrl: './announcement-body-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'nx-announcement-body-input w-full' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AnnouncementBodyInputComponent),
      multi: true
    }
  ]
})
export class AnnouncementBodyInputComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {

  @ViewChild('editorElement', { read: ElementRef }) editorElement: ElementRef;

  /** i18n-Schluessel der Beschriftung. Aufgeloest im Template ueber die translate-Pipe. */
  @Input() label: string;

  /**
   * i18n-Schluessel des Platzhaltertexts — genau wie `label`, kein vorab uebersetzter String.
   * Anders als `label` kann er aber nicht ueber eine Angular-Bindung/Pipe laufen: er geht an
   * TipTap's Placeholder-Extension, die den fertigen String beim Aufbau des Editors braucht
   * (ngAfterViewInit), nicht ueber das Template. Die Komponente loest ihn deshalb selbst per
   * TranslateService.instant() auf, bevor sie ihn an TipTap weiterreicht.
   */
  @Input() placeholder: string;

  readonly maxChars = ANNOUNCEMENT_BODY_MAX_CHARS;

  editor: Editor;
  charCount = 0;
  disabled = false;

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  /** Wert, der vor dem Bootstrap des Editors ankommt — writeValue laeuft vor ngAfterViewInit. */
  private pending: JSONContent | null = null;

  private onChange: (value: JSONContent | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get overLimit(): boolean {
    return this.charCount > this.maxChars;
  }

  /** "1.240" statt "1240" (Client-Dok §4) — Format ist unabhaengig von der UI-Sprache, siehe
   *  registerLocaleData()-Kommentar am Dateianfang. */
  get charCountFormatted(): string {
    return formatNumber(this.charCount, 'de', '1.0-0');
  }

  get maxCharsFormatted(): string {
    return formatNumber(this.maxChars, 'de', '1.0-0');
  }

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorElement.nativeElement,
      editable: !this.disabled,
      extensions: [
        ...ANNOUNCEMENT_RICH_TEXT_EXTENSIONS,
        // this.placeholder ist ein i18n-Schluessel, kein fertiger Text — siehe Doc-Kommentar
        // am Input. instant() statt der Pipe, weil TipTap den String hier braucht, nicht das
        // Template.
        Placeholder.configure({ placeholder: this.placeholder ? this.translate.instant(this.placeholder) : '' })
      ],
      content: this.pending ?? ''
    });

    this.editor.on('update', () => {
      const value = this.editor.getJSON();
      this.charCount = richTextLength(value);
      this.onChange(value);
      this.changeDetector.markForCheck();
    });

    this.editor.on('blur', () => this.onTouched());

    // Ohne das bliebe die Hervorhebung der Werkzeugleiste unter OnPush stehen: der Cursor
    // wandert in eine Ueberschrift, aber H2 sieht weiter inaktiv aus. selectionUpdate feuert
    // auch bei reiner Cursorbewegung, update nicht.
    this.editor.on('selectionUpdate', () => this.changeDetector.markForCheck());

    this.charCount = richTextLength(this.pending);
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  /**
   * emitUpdate: false ist Pflicht. Ohne das meldete das Vorbelegen beim Duplizieren eine
   * Nutzeraenderung zurueck ans Formular und markierte es sofort als dirty.
   */
  writeValue(value: JSONContent | null): void {
    this.pending = value ?? null;
    this.charCount = richTextLength(this.pending);
    if (this.editor) {
      this.editor.commands.setContent(this.pending ?? '', { emitUpdate: false });
    }
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: JSONContent | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    // setEditable emitUpdate default ist true — ohne das false hier meldete allein das
    // De-/Aktivieren einen "update" und schriebe den aktuellen Dokumentzustand als
    // Nutzeraenderung ins Control zurueck, exakt derselbe Fehler wie bei writeValue oben.
    this.editor?.setEditable(!isDisabled, false);
    this.changeDetector.markForCheck();
  }

  /**
   * editable: false auf dem Editor entfernt nur das contenteditable-Attribut — es haelt
   * TipTap-Commands, die programmatisch (nicht per Tastatur) dispatcht werden, nicht ab. Die
   * Buttons tragen deshalb zusaetzlich [disabled], aber der Schutz gehoert hierher: Buttons
   * sind nicht der einzige Aufrufer, und nur eine Sperre an der Quelle haelt den
   * ControlValueAccessor-Vertrag — ein deaktiviertes Control nimmt keine Nutzereingabe an.
   */
  toggle(command: 'bold' | 'italic' | 'bulletList' | 'orderedList' | 'blockquote'): void {
    if (this.disabled) {
      return;
    }
    const chain = this.editor.chain().focus();
    ({
      bold: () => chain.toggleBold().run(),
      italic: () => chain.toggleItalic().run(),
      bulletList: () => chain.toggleBulletList().run(),
      orderedList: () => chain.toggleOrderedList().run(),
      blockquote: () => chain.toggleBlockquote().run()
    })[command]();
  }

  toggleHeading(level: 2 | 3): void {
    if (this.disabled) {
      return;
    }
    this.editor.chain().focus().toggleHeading({ level }).run();
  }

  isActive(name: string, attrs?: Record<string, unknown>): boolean {
    return this.editor?.isActive(name, attrs) ?? false;
  }

  /**
   * Der Editor weist ein unzulaessiges Ziel selbst ab (isAllowedUri) — ein Aufruf mit
   * javascript: oder ftp: hinterlaesst dann einfach keinen Mark, der Text bleibt stehen. Ein
   * leeres Ziel entfernt den Link.
   *
   * WICHTIG: das gilt nur ueber Link.setLink()/unsetLink() — die eigenen Commands der
   * Extension. Das generische setMark('link', ...) aus @tiptap/core kennt isAllowedUri nicht
   * (das sitzt nur in Links eigenen Commands, seinen Paste-/Autolink-Plugins und in
   * renderHTML) und wuerde jedes href unvalidiert schreiben.
   */
  setLink(): void {
    if (this.disabled) {
      return;
    }
    const previous = this.editor.getAttributes('link')['href'] ?? '';
    // instant, nicht der Schluessel: window.prompt zeigt rohen Text.
    const href = window.prompt(this.translate.instant(LINK_PROMPT_KEY), previous);
    if (href === null) {
      return;
    }
    if (href === '') {
      // extendEmptyMarkRange: true (fest in unsetLink()) — ohne Auswahl entfernt es den Link
      // trotzdem vollstaendig; unsetMark('link') haette nur die Auswahl selbst bereinigt und
      // den Link um den Cursor herum stehen lassen.
      this.editor.chain().focus().unsetLink().run();
      return;
    }
    this.editor.chain().focus().setLink({ href }).run();
  }
}
